import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useSwipeDrawer } from '../../hooks/useSwipeDrawer';
import VersionBadge from './VersionBadge';
import { useMyAssignedStats, useActiveTicketsStats, useNewTicketsStats } from '../../contexts/UnifiedStatsContext';
import {
  Ticket,
  PlusCircle,
  UserCheck,
  Activity,
  List,
  Phone,
  PhoneOff,
  Archive,
  CircleDot,
  Calendar as CalendarIcon,
  FileText,
  Calculator,
  Coins,
  ClipboardCheck,
  Power,
  Bot,
  Key,
  GitBranch,
  Share2,
  Settings,
  Sparkles,
  Database,
  MessageCircle,
  Headphones,
  Building2,
  Globe,
  Moon,
  Sun,
  CalendarDays,
  Palette,
  Mail,
  Send,
  QrCode,
  GraduationCap,
} from 'lucide-react';
import { crmPhone } from '../../services/crmphone.service';
import { useOptionalTelephonyAgentEvents } from '../../contexts/TelephonyAgentEventsContext';

import TicketCreateModal from '../Tickets/TicketCreateModal';
import LeadCreateModal from '../Leads/LeadCreateModal';
// Создание инстанса SmartLombard перенесено в админку интеграций
import ConfirmModal from '../Modal/ConfirmModal';
import { useDicts } from '../../contexts/DictsContext';
import { usePermissions } from '../../utils/usePermissions';
import { useLeadsStats } from '../../contexts/UnifiedStatsContext';
import { useAppFeatures } from '../../contexts/AppFeaturesContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useWazzup24 } from '../../contexts/Wazzup24Context';
import { useSelfQr } from '../../contexts/SelfQrContext';
import { useTelegram } from '../../contexts/TelegramContext';
import { useInstagram } from '../../contexts/InstagramContext';
import { useTikTok } from '../../contexts/TikTokContext';
import { useSmartLombard } from '../../contexts/SmartLombardContext';
import smartLombardService from '../../services/smartlombard.service';
import { showToast } from '../../utils/toast';
import TransportIcon from '../WhatsApp/TransportIcon';
import { useTheme } from '../../hooks/useTheme';
import ChangelogModal from '../Changelog/ChangelogModal';
import NotificationBell from '../Notifications/NotificationBell';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import OrganizationSwitcher from './OrganizationSwitcher';
import entityEngineService from '../../services/entity-engine.service';
import { EntityIcon } from '../EntityEngine/IconPicker';
import type { EntityNavItem } from '../../types/entity-engine.types';


const Sidebar: React.FC = () => {
  const { user, logout, profile } = useAuth();
  const { permissions, hasAny, hasPermission } = usePermissions();
  const { isCollapsed: rawCollapsed, isMobile, isMobileOpen, openMobile, closeMobile, sidebarMode, isDesktopOverlayOpen, closeDesktopOverlay } = useSidebar();
  // На мобиле и в overlay сайдбар всегда показывает полную версию (с текстом)
  const isCollapsed = isMobile ? false : (isDesktopOverlayOpen ? false : rawCollapsed);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  // профиль берём из AuthContext
  const profileRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Свайп-жесты для мобильного drawer
  useSwipeDrawer({
    onOpen: openMobile,
    onClose: closeMobile,
    isOpen: isMobileOpen,
    enabled: isMobile,
  });

  // Авто-закрытие drawer при навигации
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      if (isMobile && isMobileOpen) {
        closeMobile();
      }
      if (!isMobile && isDesktopOverlayOpen) {
        closeDesktopOverlay();
      }
    }
  }, [location.pathname, isMobile, isMobileOpen, closeMobile, isDesktopOverlayOpen, closeDesktopOverlay]);

  const [isAdminSubOpen, setIsAdminSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('adminSubOpen');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isRefsSubOpen, setIsRefsSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('refsSubOpen');
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });
  const [isTicketsSubOpen, setIsTicketsSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('ticketsSubOpen');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isLeadsSubOpen, setIsLeadsSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('leadsSubOpen');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isSmartlombardSubOpen, setIsSmartlombardSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('smartlombardSubOpen');
      return saved === 'true';
    } catch {
      return true; // по умолчанию раскрыто
    }
  });
  const [isAssessmentsSubOpen, setIsAssessmentsSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('assessmentsSubOpen');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isCheckupsSubOpen, setIsCheckupsSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('checkupsSubOpen');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isGoldSubOpen, setIsGoldSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('goldSubOpen');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isWazzupSubOpen, setIsWazzupSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('wazzupSubOpen');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isSelfQrSubOpen, setIsSelfQrSubOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('selfQrSubOpen');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [isInstagramSubOpen, setIsInstagramSubOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('instagramSubOpen') === 'true';
    } catch {
      return false;
    }
  });
  const [isTikTokSubOpen, setIsTikTokSubOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('tiktokSubOpen') === 'true';
    } catch {
      return false;
    }
  });

  // Dynamic entity services — грузим только если фича entity включена в profile
  const appFeaturesEarly = useAppFeatures();
  const entityFeatureEnabled = appFeaturesEarly.entity === true;
  const [dynamicServices, setDynamicServices] = useState<EntityNavItem[]>([]);
  const fetchDynamicServices = useCallback(() => {
    if (!entityFeatureEnabled) {
      setDynamicServices([]);
      return;
    }
    entityEngineService.getActiveServices().then(setDynamicServices).catch(() => { /* entity engine may not be deployed yet */ });
  }, [entityFeatureEnabled]);
  useEffect(() => {
    fetchDynamicServices();
    if (!entityFeatureEnabled) return;
    const handler = () => fetchDynamicServices();
    window.addEventListener('entity-services:changed', handler);
    return () => window.removeEventListener('entity-services:changed', handler);
  }, [fetchDynamicServices, entityFeatureEnabled]);

  // Split dynamic services into entity vs reference/matrix groups
  const entityServices = useMemo(
    () => dynamicServices.filter((s) => !s.service_kind || s.service_kind === 'entity'),
    [dynamicServices]
  );
  const referenceServices = useMemo(
    () => dynamicServices.filter((s) => s.service_kind === 'reference' || s.service_kind === 'matrix'),
    [dynamicServices]
  );

  // no-op placeholder removed; rely on NavLink isActive + lastView on /ticket
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // профиль уже подгружается в AuthProvider; дополнительных вызовов не делаем

  const hasAutoOpened = useRef({
    admin: false,
    tickets: false,
    leads: false,
    smartlombard: false,
    assessments: false,
    checkups: false,
    gold: false,
    wazzup: false,
    selfQr: false,
  });

  useEffect(() => {
    // Авто-раскрытие подменю только при первом заходе на страницу
    if (location.pathname.startsWith('/admin') && !hasAutoOpened.current.admin) {
      setIsAdminSubOpen(true);
      hasAutoOpened.current.admin = true;
    }
    if ((location.pathname.startsWith('/tickets') || location.pathname.startsWith('/ticket')) && !hasAutoOpened.current.tickets) {
      setIsTicketsSubOpen(true);
      hasAutoOpened.current.tickets = true;
    }
    if (location.pathname.startsWith('/leads') && !hasAutoOpened.current.leads) {
      setIsLeadsSubOpen(true);
      hasAutoOpened.current.leads = true;
    }
    if (location.pathname.startsWith('/smartlombard') && !hasAutoOpened.current.smartlombard) {
      setIsSmartlombardSubOpen(true);
      hasAutoOpened.current.smartlombard = true;
    }
    if (location.pathname.startsWith('/assessments') && !hasAutoOpened.current.assessments) {
      setIsAssessmentsSubOpen(true);
      hasAutoOpened.current.assessments = true;
    }
    if (location.pathname.startsWith('/checkups') && !hasAutoOpened.current.checkups) {
      setIsCheckupsSubOpen(true);
      hasAutoOpened.current.checkups = true;
    }
    if (location.pathname.startsWith('/gold') && !hasAutoOpened.current.gold) {
      setIsGoldSubOpen(true);
      hasAutoOpened.current.gold = true;
    }
    if (location.pathname.startsWith('/wazzup24') && !hasAutoOpened.current.wazzup) {
      setIsWazzupSubOpen(true);
      hasAutoOpened.current.wazzup = true;
    }
    if (location.pathname.startsWith('/self-qr') && !hasAutoOpened.current.selfQr) {
      setIsSelfQrSubOpen(true);
      hasAutoOpened.current.selfQr = true;
    }
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem('adminSubOpen', String(isAdminSubOpen));
    } catch (e) { void e; }
  }, [isAdminSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('refsSubOpen', String(isRefsSubOpen));
    } catch (e) { void e; }
  }, [isRefsSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('ticketsSubOpen', String(isTicketsSubOpen));
    } catch (e) { void e; }
  }, [isTicketsSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('leadsSubOpen', String(isLeadsSubOpen));
    } catch (e) { void e; }
  }, [isLeadsSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('smartlombardSubOpen', String(isSmartlombardSubOpen));
    } catch (e) { void e; }
  }, [isSmartlombardSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('assessmentsSubOpen', String(isAssessmentsSubOpen));
    } catch (e) { void e; }
  }, [isAssessmentsSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('checkupsSubOpen', String(isCheckupsSubOpen));
    } catch (e) { void e; }
  }, [isCheckupsSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('goldSubOpen', String(isGoldSubOpen));
    } catch (e) { void e; }
  }, [isGoldSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('wazzupSubOpen', String(isWazzupSubOpen));
    } catch (e) { void e; }
  }, [isWazzupSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('selfQrSubOpen', String(isSelfQrSubOpen));
    } catch (e) { void e; }
  }, [isSelfQrSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('instagramSubOpen', String(isInstagramSubOpen));
    } catch (e) { void e; }
  }, [isInstagramSubOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('tiktokSubOpen', String(isTikTokSubOpen));
    } catch (e) { void e; }
  }, [isTikTokSubOpen]);

  const activeView = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/tickets/')) {
      const seg = p.split('/')[2];
      if (seg === 'new' || seg === 'assigned' || seg === 'active' || seg === 'all') return seg;
    }
    if (p.startsWith('/ticket/')) {
      try {
        const last = localStorage.getItem('ticketsLastView') || '';
        if (last === 'new' || last === 'assigned' || last === 'active' || last === 'all') return last;
      } catch (e) { void e; }
    }
    return undefined;
  }, [location.pathname]);

  const activeLeadsView = useMemo(() => {
    const p = location.pathname;
    if (p === '/leads') return 'new';
    if (p.startsWith('/leads/')) {
      const [, , seg = ''] = p.split('/');
      if (seg === 'new' || seg === 'assigned' || seg === 'my' || seg === 'all') return seg;
      if (/^\d+$/.test(seg)) {
        try {
          const stored = localStorage.getItem('leadsLastView') || '';
          if (stored === 'new' || stored === 'assigned' || stored === 'my' || stored === 'all') return stored;
        } catch (e) { void e; }
      }
    }
    return undefined;
  }, [location.pathname]);

  // Создание тикета из сайдбара (глобальная модалка)
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { dicts, ensureLoaded: ensureDictsLoaded } = useDicts();
  const canViewCreateTicket = hasPermission('tickets:create');
  const canUseCreateTicket = canViewCreateTicket;

  // Создание заявки из сайдбара
  const [isLeadCreateOpen, setIsLeadCreateOpen] = useState(false);
  const canViewCreateLead = hasPermission('leads:create');
  const canUseCreateLead = canViewCreateLead;

  // Создание инстанса Wazzup24 перенесено в админку интеграций

  // Создание инстанса SmartLombard перенесено в админку интеграций
  const canCreateSmartLombardInstance = hasPermission('integrations:smartlombard:manage');

  // Деактивация инстанса SmartLombard
  const [deactivateSmartLombardInstance, setDeactivateSmartLombardInstance] = useState<{ id: number; name: string } | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const handleConfirmDeactivateSmartLombard = async () => {
    if (!deactivateSmartLombardInstance) return;

    setIsDeactivating(true);
    try {
      await smartLombardService.setInstanceActive(deactivateSmartLombardInstance.id, false);
      showToast('Инстанс деактивирован', 'success');
      await refreshSmartlombardInstances();
      setDeactivateSmartLombardInstance(null);
    } catch (error: any) {
      showToast(error?.message || 'Ошибка деактивации', 'error');
    } finally {
      setIsDeactivating(false);
    }
  };

  // Реал-тайм число новых/активных тикетов — из глобальных провайдеров
  const { counts: newTicketsCounts } = useNewTicketsStats();
  const { counts: activeTicketsCounts } = useActiveTicketsStats();
  // Убрали локальный WS для активных — используем провайдер

  // Реал-тайм число назначенных (итого) из глобального провайдера
  const { counts: myAssignedCounts } = useMyAssignedStats();

  // Реал-тайм статистика заявок
  const { stats: leadsStats } = useLeadsStats();

  // Убрали локальный WS — используем глобальный провайдер

  // Убрали локальный WS — используем глобальный провайдер

  // Телефония: локальный переключатель видимости панели (c сохранением состояния)
  const PHONE_VIS_KEY = 'crmphone.panelVisible';
  const featureEnabled = String(import.meta.env.VITE_CRM_PHONE_ENABLED ?? 'false').toLowerCase() === 'true';
  const canViewPhone = hasPermission('telephony:panel:view');
  const canTogglePhone = hasPermission('telephony:panel:toggle');
  // Кнопка показывается только при наличии прав на переключение.
  const canSeePhoneToggle = featureEnabled && canTogglePhone;
  const [isPhoneVisible, setIsPhoneVisible] = useState<boolean>(() => {
    // Без toggle пользователь не управляет состоянием: возвращаем «всегда показан», если есть view.
    if (!canTogglePhone) return canViewPhone;
    try {
      const v = localStorage.getItem(PHONE_VIS_KEY);
      return v === 'true';
    } catch {
      return false;
    }
  });
  // Реальная видимость виджета: view обязателен, toggle позволяет управлять; иначе принудительно true.
  const effectivePhoneVisible = canViewPhone && (canTogglePhone ? isPhoneVisible : true);
  const telCtx = useOptionalTelephonyAgentEvents();
  const widgetOwnedByOtherTab = telCtx?.widgetOwnedByOtherTab ?? false;
  const collapsedProfileTitle = useMemo(() => {
    if (!isCollapsed) return undefined;
    return profile?.full_name || user?.username;
  }, [isCollapsed, profile, user]);
  // Dev helper: кнопка рендера тестовой карточки
  const togglePhonePanel = () => {
    try {
      if (isPhoneVisible) {
        // 1) Мгновенно синхронизируем состояние UI
        setIsPhoneVisible(false);
        try { localStorage.setItem(PHONE_VIS_KEY, 'false'); } catch (error) { void error; }
        try { sessionStorage.setItem('crmphone.widgetActive', 'false'); } catch (error) { void error; }
        try { window.dispatchEvent(new CustomEvent('crmphone:visibility', { detail: { visible: false } })); } catch (error) { void error; }
        // 2) Побочные эффекты
        try { telCtx?.setPanelUserHidden(true); } catch (error) { void error; }
        try { telCtx?.undock(); } catch (error) { void error; }
        try { telCtx?.toasts?.forEach(t => telCtx.minimize(t.callId)); } catch (error) { void error; }
        if (canViewPhone) {
          try { crmPhone.hide(); } catch (error) { void error; }
        }
      } else {
        // 1) Мгновенно синхронизируем состояние UI
        setIsPhoneVisible(true);
        try { localStorage.setItem(PHONE_VIS_KEY, 'true'); } catch (error) { void error; }
        try { sessionStorage.setItem('crmphone.widgetActive', 'true'); } catch (error) { void error; }
        try { window.dispatchEvent(new CustomEvent('crmphone:visibility', { detail: { visible: true } })); } catch (error) { void error; }
        // 2) Побочные эффекты
        try { telCtx?.setPanelUserHidden(false); } catch (error) { void error; }
        try { telCtx?.toasts?.forEach(t => telCtx.restore(t.callId)); } catch (error) { void error; }
        try { telCtx?.dock(); } catch (error) { void error; }
        if (canViewPhone) {
          try { void crmPhone.ensureLoaded().then(() => crmPhone.show()); } catch (error) { void error; }
        }
      }
    } catch (e) { void e; }
  };

  useEffect(() => {
    // Не перетираем сохранённую пользовательскую преференцию, если он не может управлять видимостью.
    if (!canTogglePhone) return;
    try { localStorage.setItem(PHONE_VIS_KEY, String(isPhoneVisible)); } catch { /* noop */ }
  }, [isPhoneVisible, canTogglePhone]);

  // Применяем эффективную видимость к виджету (учитывает оба пермишна)
  useEffect(() => {
    if (!featureEnabled) return; // глобально отключено — не трогаем виджет
    if (!effectivePhoneVisible) {
      // Если показывать не нужно — не грузим виджет вовсе
      return;
    }
    let cancelled = false;
    (async () => {
      await crmPhone.ensureLoaded();
      if (!cancelled) crmPhone.show();
    })();
    return () => { cancelled = true; };
  }, [effectivePhoneVisible, featureEnabled]);

  // Синхронизируемся с карточкой звонка: реакция на внешнее событие видимости
  useEffect(() => {
    const onVis = (ev: Event) => {
      try {
        const v = (ev as CustomEvent<{ visible: boolean }>).detail?.visible;
        if (typeof v === 'boolean') setIsPhoneVisible(v);
      } catch { /* noop */ }
    };
    window.addEventListener('crmphone:visibility', onVis as any);
    return () => window.removeEventListener('crmphone:visibility', onVis as any);
  }, []);

  const appFeatures = useAppFeatures();
  const { config: brandingConfig } = useBranding();
  const integrationsAvailable = appFeatures.integration === true;
  const { instances: wazzupInstances, currentInstance: currentWazzupInstance } = useWazzup24();
  const isOnWazzupRoute = location.pathname.startsWith('/wazzup24');
  const wazzupCurrentPath = isOnWazzupRoute ? location.pathname : (currentWazzupInstance ? `/wazzup24/${currentWazzupInstance.id}` : '/wazzup24');

  const { instances: selfQrInstances, currentInstance: currentSelfQrInstance } = useSelfQr();
  const { instances: telegramInstances } = useTelegram();
  const { instances: instagramInstances, currentInstance: currentInstagramInstance } = useInstagram();
  const { instances: tiktokInstances, currentInstance: currentTikTokInstance } = useTikTok();
  const isOnSelfQrRoute = location.pathname.startsWith('/self-qr');
  const selfQrCurrentPath = isOnSelfQrRoute ? location.pathname : (currentSelfQrInstance ? `/self-qr/${currentSelfQrInstance.id}` : '/self-qr');
  const isOnInstagramRoute = location.pathname.startsWith('/instagram');
  const instagramCurrentPath = isOnInstagramRoute ? location.pathname : (currentInstagramInstance ? `/instagram/${currentInstagramInstance.id}` : '/instagram');
  const isOnTikTokRoute = location.pathname.startsWith('/tiktok');
  const tiktokCurrentPath = isOnTikTokRoute ? location.pathname : (currentTikTokInstance ? `/tiktok/${currentTikTokInstance.id}` : '/tiktok');

  const { instances: smartlombardInstances, currentInstance: currentSmartlombardInstance, refreshInstances: refreshSmartlombardInstances } = useSmartLombard();
  const isOnSmartlombardRoute = location.pathname.startsWith('/smartlombard');
  const smartlombardCurrentPath = isOnSmartlombardRoute ? location.pathname : (currentSmartlombardInstance ? `/smartlombard/${currentSmartlombardInstance.id}/pawn-tickets` : '/smartlombard/pawn-tickets');

  // Пермишены для видимости разделов
  const canSeeTickets = hasPermission('tickets:list');
  const canSeeEmails = appFeatures.email === true
    && (profile?.is_admin === true || hasPermission('emails:show_module'));
  const canSeeTicketsNew = hasPermission('tickets:view:new');
  const canSeeTicketsAssigned = hasPermission('tickets:view:assigned');
  const canSeeTicketsActive = hasPermission('tickets:view:active');
  const canSeeTicketsAll = hasPermission('tickets:view:all');
  const canSeeLeads = hasAny(['leads:list']);
  const canSeeAssessments = appFeatures.assessment === true && hasAny(['assessment:show_module']);
  const canSeeCheckups = appFeatures.assessment === true && hasAny(['assessment:show_module']);
  const canSeeAgreements = appFeatures.agreement === true && hasAny(['agreements:show_module']);
  const canSeeTesting = appFeatures.testing === true;
  const canSeePaymentSchedules = appFeatures.payment_schedule === true && hasAny(['payment_schedule:show_module']);
  // AB3 Academy: видимость пункта «Обучение» — нужен и feature-flag, и permission на чтение курсов.
  // Запись на курсы (learning:enrollments:write) проверяется уже внутри страниц.
  const canSeeAcademy = appFeatures.learning === true && hasAny(['learning:courses:read']);

  const canSeeSmartlombard =
    hasAny([
      'integrations:smartlombard:pawn_tickets:list',
      'integrations:smartlombard:pawn_tickets:read',
      'integrations:smartlombard:pawn_tickets:*',
      'integrations:smartlombard:*',
    ]) ||
    (Array.isArray(permissions) && permissions.some((code) => code.startsWith('integrations:smartlombard')));
  const isSecretFeatureEnabled = appFeatures.secret;
  const canSeeStorage = isSecretFeatureEnabled && (
    hasPermission('secrets:records:list') ||              // Глобальный доступ ко всем записям
    hasPermission('secrets:records:list_granted') ||      // Доступ к записям с грантами
    // Или доступ к записям хотя бы одного шаблона
    (Array.isArray(permissions) && permissions.some(p => p.startsWith('secrets:records:list:')))
  );
  const canSeeAdmin = hasPermission('admin:panel:view');
  const canSeeWazzup = hasPermission('integrations:wazzup24:show_module');
  const canSeeSelfQr = hasPermission('integrations:self_qr:show_module') || hasPermission('integrations:self_qr:read');
  // WABA — официальный WhatsApp Business (T8/T2). У WABA нет client-side списка
  // инстансов (нет listInstances endpoint), поэтому показ зависит только от
  // feature-flag + permission. Текущий instance resolve'ится через WabaContext.
  const canSeeWaba = hasPermission('integrations:waba:show_module') || hasPermission('integrations:waba:read');
  // Telegram — личный аккаунт через MTProto. Пункт виден при включённой фиче,
  // праве read/show_module и хотя бы одном инстансе (как self_qr).
  const canSeeTelegram =
    hasPermission('integrations:telegram:show_module') ||
    hasPermission('integrations:telegram:read');
  // Instagram — канал через Meta. Пункт виден при включённой фиче и праве
  // read/show_module. Инстансы (как у telegram) появятся в задаче inbox.
  const canSeeInstagram =
    hasPermission('integrations:instagram:show_module') ||
    hasPermission('integrations:instagram:read');
  // TikTok — канал через TikTok Business Messaging API. Паттерн Instagram.
  const canSeeTikTok =
    hasPermission('integrations:tiktok:show_module') ||
    hasPermission('integrations:tiktok:read');
  const canSeeGold = appFeatures.assessment === true && hasPermission('gold:show_module');

  return (
    <aside className={[
      'sidebar',
      !isMobile && sidebarMode === 'collapsed' ? 'sidebar--collapsed' : '',
      !isMobile && sidebarMode === 'hidden' ? 'sidebar--desktop-overlay' : '',
      !isMobile && sidebarMode === 'hidden' && isDesktopOverlayOpen ? 'sidebar--desktop-overlay-open' : '',
      isMobile ? 'sidebar--mobile' : '',
      isMobile && isMobileOpen ? 'sidebar--mobile-open' : '',
    ].filter(Boolean).join(' ')}>
      <div className="sidebar-header sidebar-logo" aria-label={brandingConfig?.app_name || 'Assistant Business'}>
        <div className="sidebar-logo__mark" aria-hidden>
          <img src={brandingConfig?.logo_url || '/favicon.svg'} alt="" width="36" height="36" />
        </div>
        {!isCollapsed && (
          <div className="sidebar-logo__text">
            <span className="sidebar-logo__title">
              <span className="sidebar-logo__title-primary">
                {brandingConfig?.app_name || 'Assistant'}
              </span>
              {!brandingConfig && (
                <span className="sidebar-logo__title-secondary">Business</span>
              )}
            </span>
            {!brandingConfig && (
              <span className="sidebar-logo__subtitle">цифровая платформа</span>
            )}
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        <NavLink
          to="/"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive ? 'active' : ''}`
          }
          title={isCollapsed ? "Главная страница" : undefined}
        >
          <svg className="sidebar-nav-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        {!isCollapsed && <span>Главная страница</span>}
        </NavLink>

        {/* Leads */}
        {appFeatures.leads && canSeeLeads && (
          <>
            <NavLink
              to="/leads/new"
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive || location.pathname.startsWith('/leads') ? 'active' : ''}`
              }
              title={isCollapsed ? "Заявки" : undefined}
            >
              <List className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
              {isCollapsed && canViewCreateLead && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canUseCreateLead) setIsLeadCreateOpen(true); }}
                  onMouseDown={(e) => {
                    try {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'currentColor';
                      (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                    } catch (err) { void err; }
                  }}
                  onMouseUp={(e) => {
                    try {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                      (e.currentTarget as HTMLButtonElement).style.color = 'inherit';
                    } catch (err) { void err; }
                  }}
                  onMouseLeave={(e) => {
                    try {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                      (e.currentTarget as HTMLButtonElement).style.color = 'inherit';
                    } catch (err) { void err; }
                  }}
                  aria-label="Создать заявку"
                  title="Создать заявку"
                  className="ml-auto p-0 border-none bg-transparent text-inherit d-inline-flex items-center justify-center" style={{ width: 20, height: 20 }}
                  disabled={!canUseCreateLead}
                >
                  <PlusCircle size={14} strokeWidth={1.8} aria-hidden />
                </button>
              )}
              {!isCollapsed && (
                <>
                  <span>Заявки</span>
                  {canViewCreateLead && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canUseCreateLead) setIsLeadCreateOpen(true); }}
                      onMouseDown={(e) => {
                        try {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'currentColor';
                          (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                        } catch (err) { void err; }
                      }}
                      onMouseUp={(e) => {
                        try {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                          (e.currentTarget as HTMLButtonElement).style.color = 'inherit';
                        } catch (err) { void err; }
                      }}
                      onMouseLeave={(e) => {
                        try {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                          (e.currentTarget as HTMLButtonElement).style.color = 'inherit';
                        } catch (err) { void err; }
                      }}
                      aria-label="Создать заявку"
                      title="Создать заявку"
                      className="ml-8px rounded-lg bg-transparent text-inherit text-11 d-inline-flex items-center align-middle sidebar-create-mini-btn" style={{ padding: '2px 8px', border: '1px solid currentColor', lineHeight: 1, transform: 'translateY(1px)' }}
                      disabled={!canUseCreateLead}
                    >
                      Создать
                    </button>
                  )}
                  <button
                    className="sidebar-subnav-toggle"
                    title={isLeadsSubOpen ? 'Свернуть' : 'Развернуть'}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsLeadsSubOpen((prev) => !prev); }}
                    aria-label="Переключить подменю"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ transition: 'transform 0.2s', transform: isLeadsSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </>
              )}
            </NavLink>
            {!isCollapsed && isLeadsSubOpen && (
              <div className="sidebar-subnav">
                {hasPermission('leads:view:new') && (
                  <NavLink
                    to="/leads/new"
                    className={() => `sidebar-subnav-item ${activeLeadsView === 'new' ? 'active' : ''}`}
                  >
                    <CircleDot size={16} className="mr-8px" aria-hidden />
                    <span>Новые заявки</span>
                    {typeof leadsStats.new_leads.count === 'number' && (
                      <span className="badge badge-default ml-8px">{leadsStats.new_leads.count}</span>
                    )}
                  </NavLink>
                )}
                {hasPermission('leads:view:assigned') && (
                  <NavLink
                    to="/leads/assigned"
                    className={() => `sidebar-subnav-item ${activeLeadsView === 'assigned' ? 'active' : ''}`}
                  >
                    <Activity size={16} className="mr-8px" aria-hidden />
                    <span>Назначенные</span>
                    {typeof leadsStats.assigned_leads.count === 'number' && (
                      <span className="badge badge-default ml-8px">{leadsStats.assigned_leads.count}</span>
                    )}
                  </NavLink>
                )}
                {hasPermission('leads:view:mine') && (
                  <NavLink
                    to="/leads/my"
                    className={() => `sidebar-subnav-item ${activeLeadsView === 'my' ? 'active' : ''}`}
                  >
                    <UserCheck size={16} className="mr-8px" aria-hidden />
                    <span>Мои заявки</span>
                    {typeof leadsStats.my_leads.count === 'number' && (
                      <span className="badge badge-default ml-8px">{leadsStats.my_leads.count}</span>
                    )}
                  </NavLink>
                )}
                {hasPermission('leads:view:all') && (
                  <NavLink
                    to="/leads/all"
                    className={() => `sidebar-subnav-item ${activeLeadsView === 'all' ? 'active' : ''}`}
                  >
                    <List size={16} className="mr-8px" aria-hidden />
                    <span>Все заявки</span>
                    {typeof leadsStats.all_leads.count === 'number' && (
                      <span className="badge badge-default ml-8px">{leadsStats.all_leads.count}</span>
                    )}
                  </NavLink>
                )}
              </div>
            )}
            {isCollapsed && isLeadsSubOpen && (
              <div className="sidebar-collapsed-subnav">
                {hasPermission('leads:view:new') && (
                  <NavLink
                    to="/leads/new"
                    className={`sidebar-subnav-item ${activeLeadsView === 'new' ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                    title="Новые заявки"
                  >
                    <CircleDot size={20} strokeWidth={1.8} aria-hidden />
                    {typeof leadsStats.new_leads.count === 'number' && (
                      <span
                        className="badge badge-default absolute top-n6 right-n10"
                      >
                        {leadsStats.new_leads.count}
                      </span>
                    )}
                  </NavLink>
                )}
                {hasPermission('leads:view:assigned') && (
                  <NavLink
                    to="/leads/assigned"
                    className={`sidebar-subnav-item ${activeLeadsView === 'assigned' ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                    title="Назначенные"
                  >
                    <Activity size={20} strokeWidth={1.8} aria-hidden />
                    {typeof leadsStats.assigned_leads.count === 'number' && (
                      <span
                        className="badge badge-default absolute top-n6 right-n10"
                      >
                        {leadsStats.assigned_leads.count}
                      </span>
                    )}
                  </NavLink>
                )}
                {hasPermission('leads:view:mine') && (
                  <NavLink
                    to="/leads/my"
                    className={`sidebar-subnav-item ${activeLeadsView === 'my' ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                    title="Мои заявки"
                  >
                    <UserCheck size={20} strokeWidth={1.8} aria-hidden />
                    {typeof leadsStats.my_leads.count === 'number' && (
                      <span
                        className="badge badge-default absolute top-n6 right-n10"
                      >
                        {leadsStats.my_leads.count}
                      </span>
                    )}
                  </NavLink>
                )}
                {hasPermission('leads:view:all') && (
                  <NavLink
                    to="/leads/all"
                    className={`sidebar-subnav-item ${activeLeadsView === 'all' ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                    title="Все заявки"
                  >
                    <List size={20} strokeWidth={1.8} aria-hidden />
                    {typeof leadsStats.all_leads.count === 'number' && (
                      <span
                        className="badge badge-default absolute top-n6 right-n10"
                      >
                        {leadsStats.all_leads.count}
                      </span>
                    )}
                  </NavLink>
                )}
              </div>
            )}
          </>
        )}

        {/* Wazzup24 with submenu */}
        {appFeatures.wazzup && canSeeWazzup && (
          <>
            <NavLink
              to={wazzupCurrentPath}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive || location.pathname.startsWith('/wazzup24') ? 'active' : ''}`
              }
              title={isCollapsed ? "Wazzup24" : undefined}
            >
              <img
                src="/wazzup24-icon.png"
                alt=""
                className="sidebar-nav-item-icon"
                aria-hidden
                style={{ width: 20, height: 20 }}
              />
              {!isCollapsed && (
                <>
                  <span>Wazzup24</span>
                  <button
                    className="sidebar-subnav-toggle"
                    title={isWazzupSubOpen ? 'Свернуть' : 'Развернуть'}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsWazzupSubOpen(prev => !prev); }}
                    aria-label="Переключить подменю"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ transition: 'transform 0.2s', transform: isWazzupSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </>
              )}
            </NavLink>
            {!isCollapsed && isWazzupSubOpen && (
              <div className="sidebar-subnav">
                {wazzupInstances.map((instance) => {
                  const isActive = isOnWazzupRoute && currentWazzupInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={`/wazzup24/${instance.id}`}
                      className={() => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/wazzup24/${instance.id}`);
                      }}
                    >
                      <TransportIcon transport={instance.transport} size={16} />
                      <span>{instance.name}</span>
                      {!instance.is_active && (
                        <span className="badge badge-default ml-8px text-xs">неактивен</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
            {isCollapsed && isWazzupSubOpen && (
              <div className="sidebar-collapsed-subnav">
                {wazzupInstances.map((instance) => {
                  const isActive = isOnWazzupRoute && currentWazzupInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={`/wazzup24/${instance.id}`}
                      className={`sidebar-subnav-item ${isActive ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                      title={instance.name}
                      aria-label={instance.name}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/wazzup24/${instance.id}`);
                      }}
                    >
                      <TransportIcon transport={instance.transport} size={20} />
                      {!instance.is_active && (
                        <span
                          className="badge badge-default"
                          style={{ position: 'absolute', top: -6, right: -10, fontSize: '8px', padding: '1px 3px' }}
                        >
                          !
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Self-hosted WhatsApp QR — показываем только если есть инстансы */}
        {appFeatures.self_qr && canSeeSelfQr && selfQrInstances.length > 0 && (
          <>
            <NavLink
              to={selfQrCurrentPath}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive || location.pathname.startsWith('/self-qr') ? 'active' : ''}`
              }
              title={isCollapsed ? "WhatsApp QR" : undefined}
            >
              <QrCode size={20} className="sidebar-nav-item-icon" />
              {!isCollapsed && (
                <>
                  <span>WhatsApp QR</span>
                  <button
                    className="sidebar-subnav-toggle"
                    title={isSelfQrSubOpen ? 'Свернуть' : 'Развернуть'}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsSelfQrSubOpen(prev => !prev); }}
                    aria-label="Переключить подменю"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ transition: 'transform 0.2s', transform: isSelfQrSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </>
              )}
            </NavLink>
            {!isCollapsed && isSelfQrSubOpen && (
              <div className="sidebar-subnav">
                {selfQrInstances.map((instance) => {
                  const isActive = isOnSelfQrRoute && currentSelfQrInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={`/self-qr/${instance.id}`}
                      className={() => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/self-qr/${instance.id}`);
                      }}
                    >
                      <QrCode size={16} />
                      <span>{instance.name}</span>
                      {!instance.is_active && (
                        <span className="badge badge-default ml-8px text-xs">неактивен</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
            {isCollapsed && isSelfQrSubOpen && (
              <div className="sidebar-collapsed-subnav">
                {selfQrInstances.map((instance) => {
                  const isActive = isOnSelfQrRoute && currentSelfQrInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={`/self-qr/${instance.id}`}
                      className={`sidebar-subnav-item ${isActive ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                      title={instance.name}
                      aria-label={instance.name}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/self-qr/${instance.id}`);
                      }}
                    >
                      <QrCode size={20} />
                      {!instance.is_active && (
                        <span
                          className="badge badge-default"
                          style={{ position: 'absolute', top: -6, right: -10, fontSize: '8px', padding: '1px 3px' }}
                        >
                          !
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* WABA — официальный WhatsApp Business через Meta Cloud API */}
        {appFeatures.waba && canSeeWaba && (
          <NavLink
            to="/waba"
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive || location.pathname.startsWith('/waba') ? 'active' : ''}`
            }
            title={isCollapsed ? 'WABA' : undefined}
          >
            <MessageCircle size={20} className="sidebar-nav-item-icon" />
            {!isCollapsed && <span>WABA</span>}
          </NavLink>
        )}

        {/* Telegram — личный аккаунт через MTProto */}
        {appFeatures.telegram && canSeeTelegram && telegramInstances.length > 0 && (
          <NavLink
            to="/telegram"
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive || location.pathname.startsWith('/telegram') ? 'active' : ''}`
            }
            title={isCollapsed ? 'Telegram' : undefined}
          >
            <Send size={20} className="sidebar-nav-item-icon" />
            {!isCollapsed && <span>Telegram</span>}
          </NavLink>
        )}

        {/* Instagram — канал через Meta. Submenu инстансов (паттерн WhatsApp QR). */}
        {appFeatures.instagram && canSeeInstagram && instagramInstances.length > 0 && (
          <>
            <NavLink
              to={instagramCurrentPath}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive || isOnInstagramRoute ? 'active' : ''}`
              }
              title={isCollapsed ? 'Instagram' : undefined}
            >
              <TransportIcon transport="instagram" size={20} className="sidebar-nav-item-icon" />
              {!isCollapsed && (
                <>
                  <span>Instagram</span>
                  <button
                    className="sidebar-subnav-toggle"
                    title={isInstagramSubOpen ? 'Свернуть' : 'Развернуть'}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsInstagramSubOpen(prev => !prev); }}
                    aria-label="Переключить подменю"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ transition: 'transform 0.2s', transform: isInstagramSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </>
              )}
            </NavLink>
            {!isCollapsed && isInstagramSubOpen && (
              <div className="sidebar-subnav">
                {instagramInstances.map((instance) => {
                  const isActive = isOnInstagramRoute && currentInstagramInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={`/instagram/${instance.id}`}
                      className={() => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/instagram/${instance.id}`);
                      }}
                    >
                      <TransportIcon transport="instagram" size={16} />
                      <span>{instance.name}</span>
                      {instance.credentials?.status === 'invalid' && (
                        <span className="badge badge-default ml-8px text-xs">переподключить</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
            {isCollapsed && isInstagramSubOpen && (
              <div className="sidebar-collapsed-subnav">
                {instagramInstances.map((instance) => {
                  const isActive = isOnInstagramRoute && currentInstagramInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={`/instagram/${instance.id}`}
                      className={`sidebar-subnav-item ${isActive ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                      title={instance.name}
                      aria-label={instance.name}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/instagram/${instance.id}`);
                      }}
                    >
                      <TransportIcon transport="instagram" size={20} />
                      {instance.credentials?.status === 'invalid' && (
                        <span
                          className="badge badge-default"
                          style={{ position: 'absolute', top: -6, right: -10, fontSize: '8px', padding: '1px 3px' }}
                        >
                          !
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* TikTok — канал через TikTok Business Messaging API. Submenu инстансов (паттерн Instagram). */}
        {appFeatures.tiktok && canSeeTikTok && tiktokInstances.length > 0 && (
          <>
            <NavLink
              to={tiktokCurrentPath}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive || isOnTikTokRoute ? 'active' : ''}`
              }
              title={isCollapsed ? 'TikTok' : undefined}
            >
              <TransportIcon transport="tiktok" size={20} className="sidebar-nav-item-icon" />
              {!isCollapsed && (
                <>
                  <span>TikTok</span>
                  <button
                    className="sidebar-subnav-toggle"
                    title={isTikTokSubOpen ? 'Свернуть' : 'Развернуть'}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsTikTokSubOpen(prev => !prev); }}
                    aria-label="Переключить подменю"
                  >
                    <svg
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ transition: 'transform 0.2s', transform: isTikTokSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </>
              )}
            </NavLink>
            {!isCollapsed && isTikTokSubOpen && (
              <div className="sidebar-subnav">
                {tiktokInstances.map((instance) => {
                  const isActive = isOnTikTokRoute && currentTikTokInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={`/tiktok/${instance.id}`}
                      className={() => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/tiktok/${instance.id}`);
                      }}
                    >
                      <TransportIcon transport="tiktok" size={16} />
                      <span>{instance.name}</span>
                      {instance.credentials?.status === 'invalid' && (
                        <span className="badge badge-default ml-8px text-xs">переподключить</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
            {isCollapsed && isTikTokSubOpen && (
              <div className="sidebar-collapsed-subnav">
                {tiktokInstances.map((instance) => {
                  const isActive = isOnTikTokRoute && currentTikTokInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={`/tiktok/${instance.id}`}
                      className={`sidebar-subnav-item ${isActive ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                      title={instance.name}
                      aria-label={instance.name}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/tiktok/${instance.id}`);
                      }}
                    >
                      <TransportIcon transport="tiktok" size={20} />
                      {instance.credentials?.status === 'invalid' && (
                        <span
                          className="badge badge-default"
                          style={{ position: 'absolute', top: -6, right: -10, fontSize: '8px', padding: '1px 3px' }}
                        >
                          !
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* SmartLombard with submenu */}
        {appFeatures.smartlombard && canSeeSmartlombard && (
          <>
            <NavLink
              to={smartlombardCurrentPath}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive || location.pathname.startsWith('/smartlombard') ? 'active' : ''}`
              }
              title={isCollapsed ? "СмартЛомбард" : undefined}
            >
              <img
                src="/smartlombard-favicon.ico"
                alt=""
                className="sidebar-nav-item-icon"
                aria-hidden
              />
              {!isCollapsed && (
                <>
                  <span>СмартЛомбард</span>
                  <button
                    className="sidebar-subnav-toggle"
                    title={isSmartlombardSubOpen ? 'Свернуть' : 'Развернуть'}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsSmartlombardSubOpen(prev => !prev); }}
                    aria-label="Переключить подменю"
                  >
                    <svg
                      width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      style={{ transition: 'transform 0.2s', transform: isSmartlombardSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </>
              )}
            </NavLink>
            {!isCollapsed && isSmartlombardSubOpen && (
              <div className="sidebar-subnav">
                {smartlombardInstances.map((instance) => {
                  const instancePath = `/smartlombard/${instance.id}/pawn-tickets`;
                  const isActive = isOnSmartlombardRoute && currentSmartlombardInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={instancePath}
                      className={() => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(instancePath);
                      }}
                    >
                      <img
                        src="/smartlombard-favicon.ico"
                        alt=""
                        width={16}
                        height={16}
                        aria-hidden
                        style={{ marginRight: 8, borderRadius: 4, objectFit: 'cover' }}
                      />
                      <span className="sidebar-subnav-item-text" title={instance.name}>{instance.name}</span>
                      {canCreateSmartLombardInstance && (
                        <button
                          type="button"
                          className="sidebar-subnav-item-action"
                          title="Деактивировать инстанс"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeactivateSmartLombardInstance(instance);
                          }}
                        >
                          <Power size={14} />
                        </button>
                      )}
                    </NavLink>
                  );
                })}
                {smartlombardInstances.length === 0 && (
                  <div className="sidebar-subnav-item opacity-60 cursor-default">
                    <span>Нет доступных инстансов</span>
                  </div>
                )}
              </div>
            )}
            {isCollapsed && smartlombardInstances.length > 0 && (
              <div className="sidebar-subnav sidebar-subnav--collapsed">
                {smartlombardInstances.map((instance) => {
                  const instancePath = `/smartlombard/${instance.id}/pawn-tickets`;
                  const isActive = isOnSmartlombardRoute && currentSmartlombardInstance?.id === instance.id;
                  return (
                    <NavLink
                      key={instance.id}
                      to={instancePath}
                      className={`sidebar-subnav-item ${isActive ? 'active' : ''} d-inline-flex items-center justify-center w-24px h-24px relative`}
                      title={instance.name}
                      aria-label={instance.name}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(instancePath);
                      }}
                    >
                      <img
                        src="/smartlombard-favicon.ico"
                        alt=""
                        width={20}
                        height={20}
                        aria-hidden
                        style={{ borderRadius: 6, objectFit: 'cover' }}
                      />
                    </NavLink>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Tickets with submenu */}
        {appFeatures.tickets && canSeeTickets && (
        <>
        <NavLink
          to="/tickets/new"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith('/tickets') || location.pathname.startsWith('/ticket') ? 'active' : ''}`
          }
          title={isCollapsed ? "Тикеты" : undefined}
        >
          <Ticket className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {isCollapsed && canViewCreateTicket && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canUseCreateTicket) { void ensureDictsLoaded(); setIsCreateOpen(true); } }}
              onMouseDown={(e) => {
                try {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'currentColor';
                  (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                } catch (err) { void err; }
              }}
              onMouseUp={(e) => {
                try {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = 'inherit';
                } catch (err) { void err; }
              }}
              onMouseLeave={(e) => {
                try {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = 'inherit';
                } catch (err) { void err; }
              }}
              aria-label="Создать тикет"
              title="Создать тикет"
              className="ml-auto p-0 border-none bg-transparent text-inherit d-inline-flex items-center justify-center" style={{ width: 20, height: 20 }}
              disabled={!canUseCreateTicket}
            >
              <PlusCircle size={14} strokeWidth={1.8} aria-hidden />
            </button>
          )}
          {!isCollapsed && (
            <>
              <span>Тикеты</span>
              {canViewCreateTicket && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canUseCreateTicket) { void ensureDictsLoaded(); setIsCreateOpen(true); } }}
                onMouseDown={(e) => {
                  try {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'currentColor';
                    (e.currentTarget as HTMLButtonElement).style.color = '#fff';
                  } catch (err) { void err; }
                }}
                onMouseUp={(e) => {
                  try {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                    (e.currentTarget as HTMLButtonElement).style.color = 'inherit';
                  } catch (err) { void err; }
                }}
                onMouseLeave={(e) => {
                  try {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                    (e.currentTarget as HTMLButtonElement).style.color = 'inherit';
                  } catch (err) { void err; }
                }}
                aria-label="Создать тикет"
                title="Создать тикет"
                className="ml-8px rounded-lg bg-transparent text-inherit text-11 d-inline-flex items-center align-middle sidebar-create-mini-btn" style={{ padding: '2px 8px', border: '1px solid currentColor', lineHeight: 1, transform: 'translateY(1px)' }}
                disabled={!canUseCreateTicket}
              >
                Создать
              </button>
              )}
              <button
                className="sidebar-subnav-toggle"
                title={isTicketsSubOpen ? 'Свернуть' : 'Развернуть'}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsTicketsSubOpen(prev => !prev); }}
                aria-label="Переключить подменю"
              >
                <svg 
                  width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  style={{ transition: 'transform 0.2s', transform: isTicketsSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </>
          )}
        </NavLink>
        {!isCollapsed && isTicketsSubOpen && (
          <div className="sidebar-subnav">
            {canSeeTicketsNew && (
              <NavLink to="/tickets/new" className={() => `sidebar-subnav-item ${activeView === 'new' ? 'active' : ''}`}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 36 36"
                  className="mr-8px"
                  aria-hidden
                >
                  <path fill="currentColor" d="m34.59 23l-4.08-5l4-4.9a1.82 1.82 0 0 0 .23-1.94a1.93 1.93 0 0 0-1.8-1.16h-31A1.91 1.91 0 0 0 0 11.88v12.25A1.91 1.91 0 0 0 1.94 26h31.11a1.93 1.93 0 0 0 1.77-1.09a1.82 1.82 0 0 0-.23-1.91M2 24V12h30.78l-4.84 5.93L32.85 24Z" className="clr-i-outline clr-i-outline-path-1"/>
                  <path fill="currentColor" d="M9.39 19.35L6.13 15H5v6.18h1.13v-4.34l3.26 4.34h1.12V15H9.39z" className="clr-i-outline clr-i-outline-path-2"/>
                  <path fill="currentColor" d="M12.18 21.18h4.66v-1.02h-3.53v-1.61h3.19v-1.03h-3.19v-1.49h3.53V15h-4.66z" className="clr-i-outline clr-i-outline-path-3"/>
                  <path fill="currentColor" d="M24.52 19.43L23.06 15h-1.22l-1.47 4.43L19.05 15h-1.23l1.96 6.18h1.11l1.56-4.59L24 21.18h1.13L27.08 15h-1.23z" className="clr-i-outline clr-i-outline-path-4"/>
                  <path fill="none" d="M0 0h36v36H0z"/>
                </svg>
                <span>Новые</span>
                {typeof newTicketsCounts?.total === 'number' && (
                  <span className="badge badge-default ml-8px">{newTicketsCounts.total}</span>
                )}
              </NavLink>
            )}
            {canSeeTicketsAssigned && (
              <NavLink to="/tickets/assigned" className={() => `sidebar-subnav-item ${activeView === 'assigned' ? 'active' : ''}`}>
                <UserCheck size={16} className="mr-8px" aria-hidden />
                <span>Мои тикеты</span>
                {typeof myAssignedCounts.total === 'number' && (
                  <span className="badge badge-default ml-8px">{myAssignedCounts.total}</span>
                )}
              </NavLink>
            )}
            {canSeeTicketsActive && (
              <NavLink to="/tickets/active" className={() => `sidebar-subnav-item ${activeView === 'active' ? 'active' : ''}`}>
                <Activity size={16} className="mr-8px" aria-hidden />
                <span>Активные</span>
                {typeof activeTicketsCounts.total === 'number' && (
                  <span className="badge badge-default ml-8px">{activeTicketsCounts.total}</span>
                )}
              </NavLink>
            )}
            {canSeeTicketsAll && (
              <NavLink to="/tickets/all" className={() => `sidebar-subnav-item ${activeView === 'all' ? 'active' : ''}`}>
                <List size={16} className="mr-8px" aria-hidden />
                <span>Все тикеты</span>
              </NavLink>
            )}
          </div>
        )}
        {isCollapsed && isTicketsSubOpen && (
          <div className="sidebar-collapsed-subnav">
            {canSeeTicketsNew && (
              <NavLink
                to="/tickets/new"
                className={`sidebar-subnav-item ${activeView === 'new' ? 'active' : ''} relative`}
                title="Новые"
              >
                {/* Иконка "Новые" */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 36 36"
                  aria-hidden
                >
                  <path fill="currentColor" d="m34.59 23l-4.08-5l4-4.9a1.82 1.82 0 0 0 .23-1.94a1.93 1.93 0 0 0-1.8-1.16h-31A1.91 1.91 0 0 0 0 11.88v12.25A1.91 1.91 0 0 0 1.94 26h31.11a1.93 1.93 0 0 0 1.77-1.09a1.82 1.82 0 0 0-.23-1.91M2 24V12h30.78l-4.84 5.93L32.85 24Z" />
                  <path fill="currentColor" d="M9.39 19.35L6.13 15H5v6.18h1.13v-4.34l3.26 4.34h1.12V15H9.39z" />
                  <path fill="currentColor" d="M12.18 21.18h4.66v-1.02h-3.53v-1.61h3.19v-1.03h-3.19v-1.49h3.53V15h-4.66z" />
                  <path fill="currentColor" d="M24.52 19.43L23.06 15h-1.22l-1.47 4.43L19.05 15h-1.23l1.96 6.18h1.11l1.56-4.59L24 21.18h1.13L27.08 15h-1.23z" />
                </svg>
                {typeof newTicketsCounts?.total === 'number' && (
                  <span
                    className="badge badge-default absolute top-n6 right-n10"
                  >
                    {newTicketsCounts.total}
                  </span>
                )}
              </NavLink>
            )}
            {canSeeTicketsAssigned && (
              <NavLink
                to="/tickets/assigned"
                className={`sidebar-subnav-item ${activeView === 'assigned' ? 'active' : ''} d-inline-flex items-center w-24px h-24px relative`}
                title="Мои тикеты"
              >
                <UserCheck size={20} strokeWidth={1.8} aria-hidden />
                {typeof myAssignedCounts.total === 'number' && (
                  <span
                    className="badge badge-default absolute top-n6 right-n10"
                  >
                    {myAssignedCounts.total}
                  </span>
                )}
              </NavLink>
            )}
            {canSeeTicketsActive && (
              <NavLink
                to="/tickets/active"
                className={`sidebar-subnav-item ${activeView === 'active' ? 'active' : ''} relative`}
                title="Активные"
              >
                <Activity size={20} strokeWidth={1.8} aria-hidden />
                {typeof activeTicketsCounts.total === 'number' && (
                  <span
                    className="badge badge-default absolute top-n6 right-n10"
                  >
                    {activeTicketsCounts.total}
                  </span>
                )}
              </NavLink>
            )}
            {canSeeTicketsAll && (
              <NavLink
                to="/tickets/all"
                className={() => `sidebar-subnav-item ${activeView === 'all' ? 'active' : ''}`}
                title="Все тикеты"
                style={{}}
              >
                <List size={20} strokeWidth={1.8} aria-hidden />
              </NavLink>
            )}
          </div>
        )}
        </>
        )}

        {/* Клиенты скрыты из сайдбара */}

        {/* Email письма */}
        {canSeeEmails && (
          <NavLink
            to="/emails"
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive || location.pathname.startsWith('/emails') ? 'active' : ''}`
            }
            title={isCollapsed ? "Email письма" : undefined}
          >
            <Mail className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
            {!isCollapsed && <span>Email письма</span>}
          </NavLink>
        )}

        {canSeeAssessments && (
        <>
        <NavLink
          to="/assessments"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith('/assessments') ? 'active' : ''}`
          }
          title={isCollapsed ? "Оценка устройств" : undefined}
        >
          <Calculator className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {!isCollapsed && (
            <>
              <span>Оценка устройств</span>
              <button
                className="sidebar-subnav-toggle"
                title={isAssessmentsSubOpen ? 'Свернуть' : 'Развернуть'}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsAssessmentsSubOpen(prev => !prev); }}
                aria-label="Переключить подменю"
              >
                <svg
                  width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  style={{ transition: 'transform 0.2s', transform: isAssessmentsSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </>
          )}
        </NavLink>
        {!isCollapsed && isAssessmentsSubOpen && (
          <div className="sidebar-subnav">
            <NavLink
              to="/assessments"
              className={() => {
                const isAssessmentsRoot = location.pathname === '/assessments';
                const isAssessmentsDetail = location.pathname.startsWith('/assessments/') && !location.pathname.startsWith('/assessments/dicts');
                return `sidebar-subnav-item ${isAssessmentsRoot || isAssessmentsDetail ? 'active' : ''}`;
              }}
            >
              <Calculator size={16} className="mr-8px" aria-hidden />
              <span>Калькулятор</span>
            </NavLink>
{hasPermission('assessments:dicts:manage') && (
            <NavLink
              to="/assessments/dicts"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M19 4v16h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12z" />
                <path d="M19 16h-12a2 2 0 0 0 -2 2" />
                <path d="M9 8h6" />
              </svg>
              <span>Справочники</span>
            </NavLink>
            )}
          </div>
        )}
        </>
        )}

        {canSeeCheckups && (
        <>
        <NavLink
          to="/checkups"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith('/checkups') ? 'active' : ''}`
          }
          title={isCollapsed ? "Чек-ап Garderob" : undefined}
        >
          <ClipboardCheck className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {!isCollapsed && (
            <>
              <span>Чек-ап Garderob</span>
              <button
                className="sidebar-subnav-toggle"
                title={isCheckupsSubOpen ? 'Свернуть' : 'Развернуть'}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsCheckupsSubOpen(prev => !prev); }}
                aria-label="Переключить подменю"
              >
                <svg
                  width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  style={{ transition: 'transform 0.2s', transform: isCheckupsSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </>
          )}
        </NavLink>
        {!isCollapsed && isCheckupsSubOpen && (
          <div className="sidebar-subnav">
            <NavLink
              to="/checkups"
              className={() => {
                const isCheckupsRoot = location.pathname === '/checkups';
                const isCheckupsDetail = location.pathname.startsWith('/checkups/') && !location.pathname.startsWith('/checkups/dicts');
                return `sidebar-subnav-item ${isCheckupsRoot || isCheckupsDetail ? 'active' : ''}`;
              }}
            >
              <ClipboardCheck size={16} className="mr-8px" aria-hidden />
              <span>Чек-ап</span>
            </NavLink>
{hasPermission('checkups:dicts:manage') && (
            <NavLink
              to="/checkups/dicts"
              className={({ isActive }) => `sidebar-subnav-item ${isActive || location.pathname.startsWith('/checkups/dicts') ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M19 4v16h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12z" />
                <path d="M19 16h-12a2 2 0 0 0 -2 2" />
                <path d="M9 8h6" />
              </svg>
              <span>Справочники</span>
            </NavLink>
            )}
          </div>
        )}
        </>
        )}

        {/* Золото */}
        {canSeeGold && (
        <>
        <NavLink
          to="/gold"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith('/gold') ? 'active' : ''}`
          }
          title={isCollapsed ? 'Золото' : undefined}
        >
          <Coins className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {!isCollapsed && (
            <>
              <span>Золото</span>
              <button
                className="sidebar-subnav-toggle"
                title={isGoldSubOpen ? 'Свернуть' : 'Развернуть'}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsGoldSubOpen(prev => !prev); }}
                aria-label="Переключить подменю"
              >
                <svg
                  width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  style={{ transition: 'transform 0.2s', transform: isGoldSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </>
          )}
        </NavLink>
        {!isCollapsed && isGoldSubOpen && (
          <div className="sidebar-subnav">
            <NavLink
              to="/gold"
              className={() => {
                const isRoot = location.pathname === '/gold';
                const isDetail = location.pathname.startsWith('/gold/') && !location.pathname.startsWith('/gold/dicts');
                return `sidebar-subnav-item ${isRoot || isDetail ? 'active' : ''}`;
              }}
            >
              <Calculator size={16} className="mr-8px" aria-hidden />
              <span>Калькулятор</span>
            </NavLink>
            <NavLink
              to="/gold/dicts"
              className={({ isActive }) => `sidebar-subnav-item ${isActive || location.pathname.startsWith('/gold/dicts') ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M19 4v16h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12z" />
                <path d="M19 16h-12a2 2 0 0 0 -2 2" />
                <path d="M9 8h6" />
              </svg>
              <span>Справочники</span>
            </NavLink>
          </div>
        )}
        </>
        )}

        {/* Тестирование / Сертификация */}
        {canSeeTesting && (
        <NavLink
          to="/testing"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname === '/testing' ? 'active' : ''}`
          }
          title={isCollapsed ? "Тестирование" : undefined}
        >
          <ClipboardCheck className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {!isCollapsed && <span>Тестирование</span>}
        </NavLink>
        )}

        {/* AB3 Academy — корпоративные курсы (Phase 2) */}
        {canSeeAcademy && (
        <NavLink
          to="/academy"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith('/academy') ? 'active' : ''}`
          }
          title={isCollapsed ? "Обучение" : undefined}
        >
          <GraduationCap className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {!isCollapsed && <span>Обучение</span>}
        </NavLink>
        )}

        {canSeeAgreements && (
        <NavLink
          to="/agreements"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive ? 'active' : ''}`
          }
          title={isCollapsed ? "Договора" : undefined}
        >
          <FileText className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {!isCollapsed && <span>Договора</span>}
        </NavLink>
        )}

        {/* Графики платежей */}
        {canSeePaymentSchedules && (
        <NavLink
          to="/payment-schedules"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith('/payment-schedules') ? 'active' : ''}`
          }
          title={isCollapsed ? "Графики платежей" : undefined}
        >
          <Calculator className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {!isCollapsed && <span>Графики платежей</span>}
        </NavLink>
        )}

        {canSeeStorage && (
        <NavLink
          to="/storage"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith('/storage') ? 'active' : ''}`
          }
          title={isCollapsed ? "Хранилище" : undefined}
        >
          <Archive className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
          {!isCollapsed && <span>Хранилище</span>}
        </NavLink>
        )}

        {/* WorkflowKit (v2.42+) — заменил старый /scheduler */}
        {appFeatures.task && (
          <NavLink
            to="/tasks"
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive ? 'active' : ''}`
            }
            title={isCollapsed ? "Задачи" : undefined}
          >
            <CalendarIcon className="sidebar-nav-item-icon" size={20} strokeWidth={1.8} />
            {!isCollapsed && <span>Задачи</span>}
          </NavLink>
        )}

        {appFeatures.import_data && hasPermission('import:show_module') && (
        <NavLink
          to="/import"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive ? 'active' : ''}`
          }
          title={isCollapsed ? "Импорт" : undefined}
        >
          <svg className="sidebar-nav-item-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {!isCollapsed && <span>Импорт</span>}
        </NavLink>
        )}

        {/* Dynamic Entity Services (kind=entity) */}
        {entityServices.length > 0 && entityServices.map((svc) => (
        <NavLink
          key={svc.code}
          to={`/entities/${svc.code}`}
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith(`/entities/${svc.code}`) ? 'active' : ''}`
          }
          title={isCollapsed ? svc.name : undefined}
        >
          <span className="sidebar-nav-item-icon"><EntityIcon name={svc.icon} size={20} /></span>
          {!isCollapsed && <span>{svc.name}</span>}
        </NavLink>
        ))}

        {/* Reference + Matrix Services — collapsible "Справочники" section */}
        {referenceServices.length > 0 && (
        <>
        <button
          className={`sidebar-nav-item w-full text-left ${referenceServices.some((s) => location.pathname.startsWith(`/entities/${s.code}`)) ? 'active' : ''}`}
          onClick={() => setIsRefsSubOpen(prev => !prev)}
          title={isCollapsed ? 'Справочники' : undefined}
        >
          <svg className="sidebar-nav-item-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
          </svg>
          {!isCollapsed && (
            <>
              <span>Справочники</span>
              <span
                className="sidebar-subnav-toggle"
                aria-hidden="true"
              >
                <svg
                  width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  style={{ transition: 'transform 0.2s', transform: isRefsSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </>
          )}
        </button>
        {!isCollapsed && isRefsSubOpen && (
          <div className="sidebar-subnav">
            {referenceServices.map((svc) => (
            <NavLink
              key={svc.code}
              to={`/entities/${svc.code}`}
              className={({ isActive }) =>
                `sidebar-subnav-item ${isActive || location.pathname.startsWith(`/entities/${svc.code}`) ? 'active' : ''}`
              }
            >
              <span className="mr-8px d-inline-flex text-gray-500">
                {svc.service_kind === 'matrix' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/><path d="M15 3v18"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                )}
              </span>
              <span>{svc.name}</span>
            </NavLink>
            ))}
          </div>
        )}
        </>
        )}

        {/* Administration with submenu (moved inside main nav) */}
        {canSeeAdmin && (
        <>
        <NavLink
          to="/admin/dicts"
          className={({ isActive }) =>
            `sidebar-nav-item ${isActive || location.pathname.startsWith('/admin') ? 'active' : ''}`
          }
          title={isCollapsed ? "Администрирование" : undefined}
        >
          <svg className="sidebar-nav-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
            <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
          </svg>
          {!isCollapsed && (
            <>
              <span>Администрирование</span>
              <button
                className="sidebar-subnav-toggle"
                title={isAdminSubOpen ? 'Свернуть' : 'Развернуть'}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsAdminSubOpen(prev => !prev); }}
                aria-label="Переключить подменю"
              >
                <svg 
                  width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  style={{ transition: 'transform 0.2s', transform: isAdminSubOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </>
          )}
        </NavLink>
        {!isCollapsed && isAdminSubOpen && (
          <div className="sidebar-subnav">
            {hasAny(['dicts:registry:list']) && (
            <NavLink
              to="/admin/dicts"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M19 4v16h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12z" />
                <path d="M19 16h-12a2 2 0 0 0 -2 2" />
                <path d="M9 8h6" />
              </svg>
              <span>Справочники</span>
            </NavLink>
            )}
            {hasAny(['document_templates:categories:list']) && (
            <NavLink
              to="/admin/document-template-categories"
              className={({ isActive }) =>
                `sidebar-subnav-item ${
                  isActive ||
                  location.pathname.startsWith('/admin/document-template-categories') ||
                  location.pathname.startsWith('/admin/document-templates/')
                  ? 'active' : ''
                }`
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                <path d="M9 7l1 0" />
                <path d="M9 13l6 0" />
                <path d="M9 17l6 0" />
              </svg>
              <span>Шаблоны документов</span>
            </NavLink>
            )}
            {canSeeTesting && (profile?.is_admin || hasPermission('testing:templates:write')) && (
            <NavLink
              to="/admin/test-templates"
              className={({ isActive }) =>
                `sidebar-subnav-item ${
                  isActive || location.pathname.startsWith('/admin/test-templates')
                    ? 'active'
                    : ''
                }`
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="2" />
                <path d="M9 14l2 2l4 -4" />
              </svg>
              <span>Шаблоны тестов</span>
            </NavLink>
            )}
            {hasAny(['groups:list']) && (
            <NavLink
              to="/admin/groups"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />
                <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                <path d="M17 10h2a2 2 0 0 1 2 2v1" />
                <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
              </svg>
              <span>Группы пользователей</span>
            </NavLink>
            )}
            {hasAny(['templates:groups:list']) && (
            <NavLink
              to="/admin/message-template-groups"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6l9-6" />
              </svg>
              <span>Шаблоны сообщений</span>
            </NavLink>
            )}
            {isSecretFeatureEnabled && hasAny(['secrets:templates:list']) && (
            <NavLink
              to="/admin/secret-templates"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M16 21v-2a4 4 0 0 0 -4 -4h-4a4 4 0 0 0 -4 4v2" />
                <circle cx="8" cy="7" r="4" />
                <path d="M16 11h6m-3 -3v6" />
              </svg>
              <span>Шаблоны секретов</span>
            </NavLink>
            )}
            {hasAny(['tasks:admin_config']) && (
            <NavLink
              to="/admin/workflow"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <line x1="6" y1="3" x2="6" y2="15" />
                <circle cx="18" cy="6" r="3" />
                <circle cx="6" cy="18" r="3" />
                <path d="M18 9a9 9 0 0 1-9 9" />
              </svg>
              <span>Настройка workflow</span>
            </NavLink>
            )}
            {hasAny(['tasks:admin_config']) && (
            <NavLink
              to="/admin/workflow-analytics"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path d="M3 3v18h18" />
                <path d="M7 16l4-4 4 4 5-5" />
              </svg>
              <span>Аналитика workflow</span>
            </NavLink>
            )}
            {appFeatures.learning && hasAny(['learning:courses:write']) && (
            <NavLink
              to="/admin/academy/courses"
              className={({ isActive }) =>
                `sidebar-subnav-item ${isActive || location.pathname.startsWith('/admin/academy') ? 'active' : ''}`
              }
            >
              <GraduationCap size={16} className="mr-8px" aria-hidden />
              <span>Управление курсами</span>
            </NavLink>
            )}
            {hasAny(['rbac:roles:list']) && profile?.is_admin && (
            <NavLink
              to="/admin/roles"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M9 12l2 2l4 -4" />
                <path d="M12 2a10 10 0 1 0 10 10a10 10 0 0 0 -10 -10" />
              </svg>
              <span>Роли</span>
            </NavLink>
            )}
            {profile?.is_admin && (
            <NavLink
              to="/admin/agent-monitor"
              className={({ isActive }) =>
                `sidebar-subnav-item ${isActive || location.pathname.startsWith('/admin/agent-monitor') ? 'active' : ''}`
              }
            >
              <Activity size={16} className="mr-8px" aria-hidden />
              <span>Мониторинг агента</span>
            </NavLink>
            )}
            {hasAny(['stages:list']) && (
            <NavLink
              to="/admin/stages"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M3 12h3" />
                <path d="M12 3v3" />
                <path d="M7.8 7.8L5.6 5.6" />
                <path d="M16.2 7.8l2.2-2.2" />
                <path d="M7.8 16.2l-2.2 2.2" />
                <path d="M12 12l9 3l-4 2l-2 4l-3-9" />
              </svg>
              <span>Этапы</span>
            </NavLink>
            )}
            {hasAny(['actions:list']) && (
            <NavLink
              to="/admin/actions"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3" />
                <path d="M12 12l8-4.5" />
                <path d="M12 12v9" />
                <path d="M12 12L4 7.5" />
              </svg>
              <span>Действия</span>
            </NavLink>
            )}
            {appFeatures.shift && hasAny(['scheduler:work_schedule']) && (
            <NavLink
              to="/admin/work-schedule"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <CalendarDays size={16} className="mr-8px" />
              <span>График работы</span>
            </NavLink>
            )}
            {appFeatures.shift && hasAny(['scheduler:settings']) && (
            <NavLink
              to="/admin/schedule-settings"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4" />
                <path d="M8 2v4" />
                <path d="M3 10h18" />
              </svg>
              <span>Настройки графика</span>
            </NavLink>
            )}
            {appFeatures.shift && profile?.is_admin && (
            <NavLink
              to="/admin/work-monitor"
              className={({ isActive }) => `sidebar-subnav-item ${isActive || location.pathname.startsWith('/admin/work-monitor') ? 'active' : ''}`}
            >
              <Activity size={16} className="mr-8px" aria-hidden />
              <span>Мониторинг</span>
            </NavLink>
            )}
            {hasAny(['presence-statuses:list']) && (
            <NavLink
              to="/admin/presence-statuses"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <CircleDot size={16} className="mr-8px" aria-hidden />
              <span>Статусы пользователей</span>
            </NavLink>
            )}
            {hasAny(['users:list']) && (
            <NavLink
              to="/admin/users"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mr-8px"
                aria-hidden
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M12 12a4 4 0 1 0 -4 -4a4 4 0 0 0 4 4" />
                <path d="M6 21v-1a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v1" />
              </svg>
              <span>Пользователи</span>
            </NavLink>
            )}
            {profile?.is_admin && (
            <NavLink
              to="/admin/organizations"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Building2 size={16} className="mr-8px" aria-hidden />
              <span>Организации</span>
            </NavLink>
            )}
            {appFeatures.agreement && hasAny(['dialer:campaigns:list']) && (
            <NavLink
              to="/admin/dialer"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Phone size={16} className="mr-8px" aria-hidden />
              <span>Автодайлер</span>
            </NavLink>
            )}
            {profile?.is_admin && (
            <NavLink
              to="/admin/api-keys"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Key size={16} className="mr-8px" aria-hidden />
              <span>API ключи</span>
            </NavLink>
            )}
            {profile?.is_admin && (
            <NavLink
              to="/admin/branding"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Palette size={16} className="mr-8px" aria-hidden />
              <span>Брендинг</span>
            </NavLink>
            )}
            {profile?.is_admin && (
            <NavLink
              to="/admin/b2b"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Globe size={16} className="mr-8px" aria-hidden />
              <span>B2B Партнёры</span>
            </NavLink>
            )}
            {profile?.is_admin && appFeatures.ai_agent && (
            <NavLink
              to="/agents"
              end
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Bot size={16} className="mr-8px" aria-hidden />
              <span>Агенты</span>
            </NavLink>
            )}
            {profile?.is_admin && appFeatures.ai_agent && (
            <NavLink
              to="/agents/board"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Bot size={16} className="mr-8px" aria-hidden />
              <span>Доска сессий</span>
            </NavLink>
            )}
            {profile?.is_admin && appFeatures.ai_agent && (
            <NavLink
              to="/agent/skills"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Bot size={16} className="mr-8px" aria-hidden />
              <span>Навыки AI</span>
            </NavLink>
            )}
            {profile?.is_admin && integrationsAvailable && (
            <NavLink
              to="/admin/integrations"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Settings size={16} className="mr-8px" aria-hidden />
              <span>Интеграции</span>
            </NavLink>
            )}
            {/* CPA перенесён внутрь /admin/integrations?tab=cpa */}
            {profile?.is_admin && (
            <NavLink
              to="/admin/entity-relations"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <GitBranch size={16} className="mr-8px" aria-hidden />
              <span>Связи сущностей</span>
            </NavLink>
            )}
            {profile?.is_admin && (
            <NavLink
              to="/admin/allowed-tables"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Database size={16} className="mr-8px" aria-hidden />
              <span>Разрешённые таблицы</span>
            </NavLink>
            )}
            {profile?.is_admin && appFeatures.strategies && (
            <NavLink
              to="/admin/user-strategies"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Share2 size={16} className="mr-8px" aria-hidden />
              <span>Стратегии</span>
            </NavLink>
            )}
            {profile?.is_admin && (
            <NavLink
              to="/admin/support"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Headphones size={16} className="mr-8px" aria-hidden />
              <span>Все обращения</span>
            </NavLink>
            )}
            {profile?.is_admin && (
            <NavLink
              to="/entities/services"
              className={({ isActive }) => `sidebar-subnav-item ${isActive ? 'active' : ''}`}
            >
              <Settings size={16} className="mr-8px" aria-hidden />
              <span>Конструктор сервисов</span>
            </NavLink>
            )}
          </div>
        )}
        </>
        )}
      </nav>
      
      <div className="sidebar-footer">
        <div className="sidebar-footer-inner">
          {canSeePhoneToggle && (
            <div style={{ padding: isCollapsed ? '6px 8px' : '6px 12px' }}>
              <button
                type="button"
                className={`${`btn-ghost btn-outline btn-full ${isPhoneVisible ? 'btn-ghost--primary' : ''} inline-flex-center-g8`}`}
                onClick={togglePhonePanel}
                title={widgetOwnedByOtherTab && !isPhoneVisible ? 'Телефон открыт в другой вкладке' : isPhoneVisible ? 'Скрыть телефон' : 'Показать телефон'}
                aria-label={isPhoneVisible ? 'Скрыть телефон' : 'Показать телефон'}
                aria-pressed={isPhoneVisible}
                disabled={widgetOwnedByOtherTab && !isPhoneVisible}
              >
                {isPhoneVisible ? (
                  <Phone size={16} aria-hidden />
                ) : (
                  <PhoneOff size={16} aria-hidden />
                )}
                {!isCollapsed && <span>{widgetOwnedByOtherTab && !isPhoneVisible ? 'Открыт в другой вкладке' : isPhoneVisible ? 'Скрыть телефон' : 'Показать телефон'}</span>}
              </button>
            </div>
          )}
          <OrganizationSwitcher isCollapsed={isCollapsed} />
          <div className="profile-card" ref={profileRef}>
            <div
              className="profile-info cursor-pointer"
              onClick={() => { setIsProfileMenuOpen(!isProfileMenuOpen); setIsNotifOpen(false); }}

              title={collapsedProfileTitle}
            >
              <div className="profile-avatar-wrapper">
                <div className="profile-avatar">
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt={profile.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  ) : (
                    (profile?.full_name || user?.username)?.charAt(0).toUpperCase()
                  )}
                </div>
              </div>
              {!isCollapsed && (
                <>
                  <div className="profile-details">
                    <div className="profile-name">{profile?.full_name || user?.username}</div>
                    {profile?.email && (
                      <div className="profile-email text-gray-600 text-xs">
                        {profile.email}
                      </div>
                    )}
                  </div>
                  {appFeatures.notifications && (
                    <div className="profile-notif-bell" onClick={(e) => e.stopPropagation()}>
                      <NotificationBell
                        variant="sidebar"
                        isOpen={isNotifOpen}
                        onClick={() => { setIsNotifOpen(o => !o); setIsProfileMenuOpen(false); }}
                      />
                    </div>
                  )}
                  <svg
                    className="profile-menu-arrow"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{
                      transition: 'transform 0.2s',
                      transform: isProfileMenuOpen ? 'rotate(180deg)' : 'rotate(0)'
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </div>
            
            {isProfileMenuOpen && (
              <div className="profile-dropdown">
                <div className="profile-dropdown-version">
                  <VersionBadge />
                </div>
                <div className="profile-dropdown-divider"></div>
                {profile?.roles?.some((r: any) => r.code === 'superadmin') && (
                  <button className="profile-dropdown-item" onClick={() => { setIsChangelogOpen(true); setIsProfileMenuOpen(false); }}>
                    <Sparkles size={16} />
                    <span>Что нового?</span>
                  </button>
                )}
                <button className="profile-dropdown-item" onClick={() => { setIsProfileMenuOpen(false); navigate('/support'); }}>
                  <MessageCircle size={16} />
                  <span>Мои обращения</span>
                </button>
                <button className="profile-dropdown-item" onClick={() => { toggleTheme(); }}>
                  {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
                  <span>{theme === 'light' ? 'Тёмная тема' : 'Светлая тема'}</span>
                </button>
                {appFeatures.notifications && (
                  <button className="profile-dropdown-item" onClick={() => { setIsProfileMenuOpen(false); navigate('/settings/notifications'); }}>
                    <Settings size={16} />
                    <span>Настройки уведомлений</span>
                  </button>
                )}
                <div className="profile-dropdown-divider"></div>
                <button className="profile-dropdown-item" onClick={logout}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Выйти</span>
                </button>
              </div>
            )}
            {appFeatures.notifications && isNotifOpen && (
              <NotificationDropdown onClose={() => setIsNotifOpen(false)} />
            )}
          </div>
        </div>
      </div>
      {/* Глобальная модалка создания тикета из сайдбара */}
      {/* Показываем только при наличии права на использование */}
      {canUseCreateTicket && (
        <TicketCreateModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          dicts={dicts}
          onCreated={(t) => {
            setIsCreateOpen(false);
            navigate(`/ticket/${t.id}`);
            try { window.dispatchEvent(new Event('tickets:changed' as any)); } catch (e) { void e; }
          }}
        />
      )}
      {/* Глобальная модалка создания заявки из сайдбара */}
      {canUseCreateLead && (
        <LeadCreateModal
          isOpen={isLeadCreateOpen}
          onClose={() => setIsLeadCreateOpen(false)}
          onCreated={(lead) => {
            setIsLeadCreateOpen(false);
            navigate(`/leads/${lead.id}`);
          }}
        />
      )}
      {/* Создание инстансов Wazzup24 и SmartLombard перенесено в админку интеграций */}
      {/* Модалка «Что нового?» — журнал изменений */}
      <ChangelogModal isOpen={isChangelogOpen} onClose={() => setIsChangelogOpen(false)} />
      {/* Модалка подтверждения деактивации инстанса SmartLombard */}
      <ConfirmModal
        isOpen={!!deactivateSmartLombardInstance}
        onClose={() => setDeactivateSmartLombardInstance(null)}
        onConfirm={handleConfirmDeactivateSmartLombard}
        title="Деактивация инстанса"
        message={`Вы уверены, что хотите деактивировать инстанс "${deactivateSmartLombardInstance?.name}"?`}
        confirmText="Деактивировать"
        cancelText="Отмена"
        isDestructive
        isLoading={isDeactivating}
      />
    </aside>
  );
};

export default Sidebar;
